import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, History, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const dt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const jour = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR') : '');

const libelleSource = (s = '') => {
  if (s.startsWith('Remise à jour')) return 'Remise à jour des reçus';
  if (/SEPA/i.test(s)) return 'Stripe — prélèvement SEPA';
  if (/lien CB/i.test(s)) return 'Stripe — lien carte de solde';
  if (/^Stripe/i.test(s)) return 'Stripe — carte bancaire';
  if (/^Klarys/i.test(s)) return 'Rapprochement Klarys';
  return s ? `Saisie admin (${s})` : 'Automatique';
};

const telecharger = async (gedId, version, headers) => {
  try {
    const r = await axios.get(`${API}/ged/documents/${gedId}/download`, { headers, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement('a'), { href: url, download: `recu_v${version}.pdf` }).click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { toast.error('Téléchargement impossible'); }
};

const Ligne = ({ v, headers }) => {
  const d = v.declencheur || {};
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-100 py-2.5 text-sm last:border-0" data-testid={`recu-version-${v.version}`}>
      <span className="w-10 font-bold text-slate-800">v{v.version}</span>
      <span className="w-32 text-slate-600">{dt(v.created_at)}</span>
      <span className="min-w-[180px] flex-1 text-slate-700">
        {d.source === 'remise_a_jour' ? 'Régénération groupée'
          : d.montant ? <>Paiement {eur(d.montant)}{d.mode ? ` · ${d.mode}` : ''}{d.date ? ` · ${jour(d.date)}` : ''}</> : '—'}
        <span className="block text-xs text-slate-500">{libelleSource(v.source)}</span>
      </span>
      <span className="text-xs text-slate-600">{eur(v.paye)} / {eur(v.total)}</span>
      <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${v.solde ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid={`recu-version-statut-${v.version}`}>
        {v.solde ? 'Acquitté' : `Partiel · reste ${eur(v.reste)}`}
      </span>
      {v.archive && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">archivée</span>}
      {v.ged_id && (
        <button type="button" onClick={() => telecharger(v.ged_id, v.version, headers)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0a3d62] hover:underline" data-testid={`recu-version-telecharger-${v.version}`}>
          <Download className="h-3.5 w-3.5" /> Télécharger
        </button>
      )}
    </li>
  );
};

export const RecuVersionsDialog = ({ produit, ident, titre, open, onOpenChange }) => {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    setItems(null);
    axios.get(`${API}/recus/versions/${produit}/${encodeURIComponent(ident)}`, { headers: getAuthHeader() })
      .then((r) => setItems(r.data.items)).catch(() => { setItems([]); toast.error('Historique indisponible'); });
  }, [open, produit, ident]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="recu-versions-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Versions du reçu — {titre}</DialogTitle></DialogHeader>
        {!items ? <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-400" />
          : !items.length ? <p className="py-6 text-center text-sm text-slate-500" data-testid="recu-versions-vide">Aucune version enregistrée : le reçu n'a pas encore été mis à jour par un encaissement.</p>
            : <ul className="max-h-[60vh] overflow-y-auto">{items.map((v) => <Ligne key={v.id} v={v} headers={getAuthHeader()} />)}</ul>}
      </DialogContent>
    </Dialog>
  );
};

export const BoutonVersionsRecu = ({ produit, ident, titre, className = '', testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={testId || `versions-recu-${ident}`}
        className={`inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 px-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 ${className}`}>
        <History className="h-3.5 w-3.5" /> Versions du reçu
      </button>
      <RecuVersionsDialog produit={produit} ident={ident} titre={titre} open={open} onOpenChange={setOpen} />
    </>
  );
};
